import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { AveragePositionPlayer, MatchXgStats, TeamRef } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { FullPitchMarkings, PitchTurf } from './pitchMarkings';

// A full horizontal (landscape) pitch, mirroring the shotmap's broadcast feel so
// the average-positions map sits visually consistent with the rest of the panel.
const PITCH_HEIGHT = 236;
const SAFE_INSET = 12;
// Refined dots: smaller than before (was 14) so two near-overlapping players stay
// legible, with a clear separation distance used by the collision-nudge pass.
const DOT_RADIUS = 11;
const MIN_SEP = DOT_RADIUS * 2 + 3; // closest two dot CENTRES may sit before nudging

// Bzzoiro positional coords are NOT a shared 0–100 full-pitch grid. `x` is each
// team's own DISTANCE-FROM-OWN-GOAL in its OWN attacking frame: the goalkeeper
// sits at the MINIMUM x (~7–14) and the deepest outfielder ~66–85, so x spans
// ~0–85 PER SIDE (verified against live match_advanced_stats). `y` IS a genuine
// 0–100 pitch-WIDTH value (verified ~16–89).
//
// So we (1) rescale x against the real span (X_SPAN, NOT /100) and (2) MIRROR per
// side so the two teams face each other across the halfway line:
//   frac = clamp(x, X_SPAN) / X_SPAN              (0 = own goal, 1 = deepest)
//   home attacks RIGHT → own goal is the LEFT edge  → xPct = frac
//   away attacks LEFT  → own goal is the RIGHT edge → xPct = 1 − frac
// Result: home keeper far LEFT, away keeper far RIGHT, both formations spread
// across the FULL pitch width. (The previous build treated x as a shared 0–100
// grid with NO mirror, so without a real own-goal frame every player piled into
// the same central band — the "all clustered in the centre" bug.)
const X_SPAN = 85;

function clamp(value: number, max: number): number {
  return Math.min(max, Math.max(0, value));
}

// A player with a resolved pixel centre + a stable key (post collision-nudge).
interface PlacedPlayer {
  key: string;
  player: AveragePositionPlayer;
  side: 'home' | 'away';
  cx: number;
  cy: number;
}

/**
 * Map every player to a pixel centre, then run a light relaxation pass so two
 * players whose average positions sit almost on top of each other are nudged
 * apart just enough that BOTH numbered dots (and their labels) stay legible —
 * without distorting the overall formation shape. A few gentle iterations of
 * pairwise push, clamped back inside the pitch, reads clean and stable.
 */
function placePlayers(
  home: AveragePositionPlayer[],
  away: AveragePositionPlayer[],
  width: number,
): PlacedPlayer[] {
  const usableW = width - SAFE_INSET * 2;
  const usableH = PITCH_HEIGHT - SAFE_INSET * 2;
  const minX = SAFE_INSET + DOT_RADIUS;
  const maxX = SAFE_INSET + usableW - DOT_RADIUS;
  const minY = SAFE_INSET + DOT_RADIUS;
  const maxY = SAFE_INSET + usableH - DOT_RADIUS;

  const placed: PlacedPlayer[] = [
    ...home.map((player, index) => ({ player, side: 'home' as const, index })),
    ...away.map((player, index) => ({ player, side: 'away' as const, index })),
  ].map(({ player, side, index }) => {
    // Distance-from-own-goal, rescaled to 0…1 against the real span.
    const frac = clamp(player.x, X_SPAN) / X_SPAN;
    // Mirror per side: home own-goal is the LEFT edge (xPct = frac); away own-goal
    // is the RIGHT edge (xPct = 1 − frac) so the two teams face across halfway.
    const xPct = side === 'home' ? frac : 1 - frac;
    return {
      key: `${side === 'home' ? 'h' : 'a'}-${player.player_id ?? `${player.n}-${index}`}`,
      player,
      side,
      cx: SAFE_INSET + xPct * usableW,
      cy: SAFE_INSET + (clamp(player.y, 100) / 100) * usableH,
    };
  });

  // 6 relaxation passes: push any overlapping pair apart by half the shortfall
  // each (so the move is shared), then clamp to keep dots on the pitch.
  for (let pass = 0; pass < 6; pass++) {
    for (let i = 0; i < placed.length; i++) {
      for (let j = i + 1; j < placed.length; j++) {
        const a = placed[i];
        const b = placed[j];
        let dx = b.cx - a.cx;
        let dy = b.cy - a.cy;
        let dist = Math.hypot(dx, dy);
        if (dist >= MIN_SEP) continue;
        // Coincident dots: deterministic tiny offset so they always split apart.
        if (dist < 0.01) {
          dx = (i % 2 === 0 ? 1 : -1) * 0.5;
          dy = (j % 2 === 0 ? 1 : -1) * 0.5;
          dist = Math.hypot(dx, dy);
        }
        const push = (MIN_SEP - dist) / 2;
        const ux = dx / dist;
        const uy = dy / dist;
        a.cx -= ux * push;
        a.cy -= uy * push;
        b.cx += ux * push;
        b.cy += uy * push;
        a.cx = Math.min(maxX, Math.max(minX, a.cx));
        a.cy = Math.min(maxY, Math.max(minY, a.cy));
        b.cx = Math.min(maxX, Math.max(minX, b.cx));
        b.cy = Math.min(maxY, Math.max(minY, b.cy));
      }
    }
  }

  return placed;
}

/**
 * One player's average-position dot: a refined tinted disc carrying the jersey
 * number, with a crisp light ring so it lifts off the grass. Tapping it reveals
 * the player's name on a small chip below; tapping again (or tapping another)
 * hides it, so the formation stays clean by default. Positioned absolutely off
 * the collision-nudged pixel CENTRE (cx, cy) computed by the parent, so two
 * near-overlapping players stay legible instead of stacking on one spot.
 */
function PositionDot({
  player,
  cx,
  cy,
  color,
  textColor,
  open,
  onPress,
}: {
  player: AveragePositionPlayer;
  cx: number;
  cy: number;
  color: string;
  textColor: string;
  open: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const LABEL_W = 76;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${player.name} · ${player.n}`}
      onPress={onPress}
      hitSlop={8}
      style={{
        position: 'absolute',
        left: cx - LABEL_W / 2,
        top: cy - DOT_RADIUS,
        width: LABEL_W,
        alignItems: 'center',
        zIndex: open ? 5 : 1,
      }}
    >
      <View
        style={{
          width: DOT_RADIUS * 2,
          height: DOT_RADIUS * 2,
          borderRadius: DOT_RADIUS,
          backgroundColor: color,
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: open ? 2 : 1.25,
          borderColor: open ? palette.voltBright : 'rgba(255,255,255,0.85)',
          // A soft contact shadow lifts every dot off the grass; the open dot
          // glows in its team hue so the tapped player stands out.
          shadowColor: open ? color : '#000000',
          shadowOffset: { width: 0, height: open ? 0 : 1 },
          shadowOpacity: open ? 0.9 : 0.35,
          shadowRadius: open ? 6 : 2,
          elevation: open ? 5 : 2,
        }}
      >
        <AppText weight="bold" tabular style={{ color: textColor, fontSize: 11 }}>
          {localizeDigits(String(player.n), arabicNumerals)}
        </AppText>
      </View>
      {open ? (
        <View className="mt-1 rounded-full bg-surface-2 px-2 py-0.5" style={{ maxWidth: LABEL_W }}>
          <AppText
            variant="micro"
            weight="bold"
            className="text-center text-ink"
            numberOfLines={1}
            style={{ fontSize: 10 }}
          >
            {player.name}
          </AppText>
        </View>
      ) : null}
    </Pressable>
  );
}

/** A team colour chip + name, for the legend strip under the title. */
function TeamKey({ color, label }: { color: string; label: string }) {
  return (
    <View className="flex-row items-center gap-1.5">
      <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: color, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.7)' }} />
      <AppText variant="micro" weight="medium" className="text-soft" numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

/**
 * Average positions — a full horizontal pitch plotting each team's average player
 * locations from `stats.average_positions`. Provider `x` is a per-team distance-
 * from-own-goal (~0–85), so we rescale + MIRROR per side: home (volt) attacks the
 * right goal (own goal at the left edge), away (coral) the left (own goal at the
 * right edge), and both spread across the full width. Each player is a numbered
 * dot you can tap to reveal a name. A plain one-line subtitle explains what the
 * map shows. The caller hides the block when the data is absent; this also self-
 * hides if BOTH sides come back empty. The pitch is forced LTR so the home/away
 * orientation never flips in RTL.
 */
export function AveragePositions({
  stats,
  homeTeam,
  awayTeam,
}: {
  stats: MatchXgStats | null;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [width, setWidth] = useState(0);
  // The single open name chip (only one shows at a time, keyed by side+index).
  const [openKey, setOpenKey] = useState<string | null>(null);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  const positions = stats?.average_positions ?? null;
  const home = positions?.home;
  const away = positions?.away;

  // Resolve every dot to a collision-nudged pixel centre once the pitch width is
  // known, so two players on (nearly) the same spot stay legible. Keyed on the
  // provider arrays directly (stable across renders) so the relaxation pass only
  // re-runs when the data or the measured width actually changes.
  const placed = useMemo(
    () => (width > 0 ? placePlayers(home ?? [], away ?? [], width) : []),
    [home, away, width],
  );

  if ((home?.length ?? 0) === 0 && (away?.length ?? 0) === 0) return null;

  return (
    <Card className="overflow-hidden p-0">
      <View className="gap-0.5 px-4 pb-1.5 pt-3">
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
          {t('match.avgPositions')}
        </AppText>
        <AppText variant="micro" className="text-muted" numberOfLines={2}>
          {t('match.avgPositionsHint')}
        </AppText>
      </View>

      {/* Team colour key — what tells the two formations apart. */}
      <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-2">
        <TeamKey color={palette.volt} label={localizeTeamName(homeTeam, locale, true)} />
        <TeamKey color={palette.teamAway} label={localizeTeamName(awayTeam, locale, true)} />
      </View>

      {/* The pitch is always read left→right (home right, away left), even in RTL. */}
      <Pressable onPress={() => setOpenKey(null)}>
        <View
          className="overflow-hidden bg-pitch"
          style={{ height: PITCH_HEIGHT, direction: 'ltr' }}
          onLayout={onLayout}
        >
          <PitchTurf orientation="horizontal" />
          {width > 0 ? (
            <FullPitchMarkings width={width} height={PITCH_HEIGHT} inset={SAFE_INSET} />
          ) : null}
          {placed.map((p) => (
            <PositionDot
              key={p.key}
              player={p.player}
              cx={p.cx}
              cy={p.cy}
              color={p.side === 'home' ? palette.volt : palette.teamAway}
              textColor={palette.onVolt}
              open={openKey === p.key}
              onPress={() => setOpenKey((prev) => (prev === p.key ? null : p.key))}
            />
          ))}
        </View>
      </Pressable>
    </Card>
  );
}
